import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _get from 'lodash/get';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Button, Container } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartShopping } from '@fortawesome/free-solid-svg-icons';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
} from '@fortawesome/free-brands-svg-icons';

import { history } from 'Redux/store';
import { currencyCode } from 'Helpers/Geo/data';
import { addToCart } from 'Redux/ducks/cart';
import Drm from 'Components/Utility/Drm';
import FluidImg from 'Components/Utility/FluidImg';
import Price from 'Components/Utility/Price/Price';

import 'Components/Cards/MysteryBundleCard.scss';
import 'Components/Utility/RangeSlider.scss';
import 'Components/Bundle/MysteryKeySelectionButtons.scss';

const MysteryBundleCard = ({ product }) => {
  const dispatch = useDispatch();
  const [selectedTier, setSelectedTier] = useState(0);
  const [addingToBasket, setAddingToBasket] = useState(false);

  const push = history.push;

  useEffect(() => {
    const bundleLength = _get(product, 'bundles.length', 0);
    const tierToSelect = (bundleLength - 1);
    setSelectedTier(tierToSelect);
  }, [product]);

  const {
    _id: productId,
    cover,
    name,
    drm,
    type,
    slug,
    platforms,
    sdescs,
    seo,
    price,
    bundles,
    cover_alt_text: coverAltText,
  } = product;

  const validDRM = Object.keys(drm).filter(p => drm[p]);
  const { locale } = window;

  const tieredMystery = _get(product, 'bundles.length', false) > 1;

  const clickAddToCart = async () => {
    setAddingToBasket(true);
    await dispatch(addToCart(push, productId, selectedTier, true));
    setAddingToBasket(false);
  };

  const onMysteryButtonClick = (value) => {
    if (value !== selectedTier) setSelectedTier(value);
  };

  const mysteryKeyTotals = bundles.map(bundle => bundle.displayTotal);
  const selectedTierObj = bundles[selectedTier] || bundles[0];

  const selectedTierObjDisplayTotal = _get(selectedTierObj, 'displayTotal', 0);
  const selectedTierObjPrice = _get(selectedTierObj, 'price', {});

  return (
    <Container>
      <div className="mystery-bundle-card">
        <div className="mystery-content-container">
          <div className="left-container">
            <div className="cover-container responsive-image-island">
              <div className="responsive-image responsive-image--16by9">
                <Link to={`/${type}/${slug}`}>
                  <FluidImg
                    ratio16by9
                    sizesOverride="(min-width: 1520px) 600px, (min-width: 1200px) 510px, (min-width: 992px) 430px, 100vw"
                    className="img-force-full"
                    src={cover}
                    alt={coverAltText || name}
                  />
                </Link>
              </div>
            </div>
          </div>
          <div className="right-container">
            <div className="mystery-details-container">
              <div className="intro-container">
                {(sdescs ? sdescs[locale] : null) || seo.desc}
              </div>
              <div className="icons-container">
                {validDRM &&
                  <Drm drmType={validDRM[0]} />
                }
                <div className="system-icons">
                  {platforms.windows &&
                    <FontAwesomeIcon icon={fabFaWindows} className="mr-2" />
                  }
                  {platforms.mac &&
                    <FontAwesomeIcon icon={fabFaApple} className="mr-2" />
                  }
                  {platforms.linux &&
                    <FontAwesomeIcon icon={fabFaLinux} />
                  }
                </div>
              </div>
              {tieredMystery &&
                <div>
                  <p className="slider-title">
                    <FormattedMessage
                      id="homepagemystery.keys-slider.header"
                      defaultMessage="{count, plural, one {# Mystery Key} other {# Mystery Keys}} for {price}"
                      values={{
                        count: selectedTierObjDisplayTotal,
                        price: <Price
                          price={selectedTierObjPrice}
                        />,
                      }}
                    />
                  </p>
                </div>
              }
              <div className="slider-button-container">
                <div className="mystery-slider-container">
                  {!tieredMystery &&
                    <div className="price-discount-container">
                      <div className="price">
                        <Price
                          price={price}
                        />
                      </div>
                    </div>
                  }

                  {tieredMystery &&
                    <div className="MysteryKeySelectionButtons mb-0">
                      <div className="MysteryKeySelectionButtons__keysButtonContainer">
                        {mysteryKeyTotals.map((mysteryTotal, tierIndex) =>
                          <button
                            key={mysteryTotal}
                            className={classNames('MysteryKeySelectionButtons__keysButton', { 'MysteryKeySelectionButtons__keysButton--selected': selectedTierObjDisplayTotal === mysteryTotal })}
                            aria-label={`Change quantity to ${mysteryTotal} mystery ${mysteryTotal === 1 ? 'key' : 'keys'} for ${bundles[tierIndex].price[currencyCode] / 100} ${currencyCode}. ${selectedTierObjDisplayTotal === mysteryTotal ? 'Selected' : 'Not selected'}`}
                            onClick={() => onMysteryButtonClick(tierIndex)}
                          >
                            {mysteryTotal}
                          </button>,
                        )}
                      </div>
                    </div>
                  }
                </div>
                <div className="button-container">
                  <Button
                    disabled={addingToBasket}
                    color="primary"
                    className="cart-btn"
                    onClick={() => clickAddToCart()}
                  >
                    <div className="cart-btn-icon">
                      <FontAwesomeIcon icon={faCartShopping} />
                    </div>
                    {!addingToBasket &&
                      <div className="btn-text-container">
                        <b>
                          <FormattedMessage
                            id="homepagemystery.keys-slider.add-btn"
                            defaultMessage="Add to cart"
                          />
                        </b>
                      </div>
                    }
                    {addingToBasket &&
                      <div className="btn-text-container">
                        <b>
                          <FormattedMessage
                            id="homepagemystery.keys-slider.adding-btn"
                            defaultMessage="Adding"
                          />
                        </b>
                      </div>
                    }
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default MysteryBundleCard;

MysteryBundleCard.propTypes = {
  product: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    cover: PropTypes.string,
    bundleCover: PropTypes.string,
    release: PropTypes.string,
    platforms: PropTypes.shape({
      windows: PropTypes.bool,
      mac: PropTypes.bool,
      linux: PropTypes.bool,
    }),
    lang: PropTypes.arrayOf(PropTypes.string),
    hideDiscount: PropTypes.bool,
    bundles: PropTypes.arrayOf(PropTypes.shape({
      price: PropTypes.shape({}),
    })),
  }),
};

MysteryBundleCard.defaultProps = {
  product: {
    _id: '',
    name: '',
    cover: 'default.jpg',
    bundles: [
      {
        price: {},
      },
    ],
    bundleCover: 'default.jpg',
    release: '',
    platforms: {
      windows: false,
      mac: false,
      linux: false,
    },
    lang: [],
    hideDiscount: false,
    drm: {},
    price: {},
  },
};
